import React, { useEffect, useState } from "react";
import { api, fmtNum, todayISO, fmtDate } from "../lib/api";
import { Plus, Trash2, Snowflake, Save } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_OFFALS = [
  { label: "Liver", unit: "kg", qty: "" },
  { label: "Kudal", unit: "kg", qty: "" },
  { label: "Suvarotti", unit: "kg", qty: "" },
  { label: "Head", unit: "piece", qty: "" },
  { label: "Trotters (Paya)", unit: "piece", qty: "" },
  { label: "Brain", unit: "piece", qty: "" },
  { label: "Lungs", unit: "piece", qty: "" },
];

export default function FreezerStock() {
  const [date, setDate] = useState(todayISO());
  const [live, setLive] = useState("");
  const [chCarc, setChCarc] = useState("");
  const [mtCarc, setMtCarc] = useState("");
  const [offals, setOffals] = useState(DEFAULT_OFFALS);
  const [soldKg, setSoldKg] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    const res = await api.get("/freezer-stock?limit=30");
    setHistory(res.data);
  };

  const loadForDate = async (d) => {
    const res = await api.get(`/freezer-stock/by-date?date=${d}`);
    const doc = res.data;
    if (doc && doc.id) {
      setLive(String(doc.live_chicken_kg || ""));
      setChCarc(String(doc.chicken_carcass_kg || ""));
      setMtCarc(String(doc.mutton_carcass_kg || ""));
      setNotes(doc.notes || "");
      // Merge stored offals into default rows
      const stored = doc.offals || [];
      const map = Object.fromEntries(stored.map((o) => [o.label, o]));
      setOffals(
        DEFAULT_OFFALS.map((d) => {
          const s = map[d.label];
          return s ? { ...d, unit: s.unit, qty: String(s.qty) } : d;
        })
      );
    } else {
      setLive("");
      setChCarc("");
      setMtCarc("");
      setSoldKg("");
      setNotes("");
      setOffals(DEFAULT_OFFALS);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    loadForDate(date);
  }, [date]);

  const save = async () => {
    const offalPayload = offals
      .filter((o) => o.label.trim() && parseFloat(o.qty) > 0)
      .map((o) => ({
        label: o.label.trim(),
        unit: o.unit,
        qty: parseFloat(o.qty),
      }));
    await api.post("/freezer-stock", {
      date,
      live_chicken_kg: parseFloat(live) || 0,
      chicken_carcass_kg: parseFloat(chCarc) || 0,
      mutton_carcass_kg: parseFloat(mtCarc) || 0,
      offals: offalPayload,
      sold_kg: parseFloat(soldKg) || 0,
      notes,
    });
    toast.success(`Freezer stock saved for ${date}`);
    loadHistory();
  };

  const updOffal = (i, field, val) => {
    setOffals((arr) => arr.map((o, idx) => (idx === i ? { ...o, [field]: val } : o)));
  };
  const addOffalRow = () =>
    setOffals((arr) => [...arr, { label: "", unit: "kg", qty: "" }]);
  const removeOffalRow = (i) =>
    setOffals((arr) => arr.filter((_, idx) => idx !== i));

  const del = async (id) => {
    if (!window.confirm("Delete this freezer stock record?")) return;
    await api.delete(`/freezer-stock/${id}`);
    loadHistory();
    if (date) loadForDate(date);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="freezer-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
          <Snowflake className="h-3 w-3" /> End-of-Day Stock
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Freezer Stock
        </h1>
        <p className="text-sm text-zinc-600 mt-2">
          Record what&apos;s left in the freezer at end of day. One snapshot per date — re-saving
          updates the same record.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="border border-zinc-200 bg-white p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="fz-date"
                className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Live Chicken (kg)
                </label>
                <input
                  type="number" step="0.01" value={live}
                  onChange={(e) => setLive(e.target.value)}
                  data-testid="fz-live"
                  className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num text-lg focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Chicken Carcass (kg)
                </label>
                <input
                  type="number" step="0.01" value={chCarc}
                  onChange={(e) => setChCarc(e.target.value)}
                  data-testid="fz-chcarc"
                  className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num text-lg focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Mutton Carcass (kg)
                </label>
                <input
                  type="number" step="0.01" value={mtCarc}
                  onChange={(e) => setMtCarc(e.target.value)}
                  data-testid="fz-mtcarc"
                  className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num text-lg focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
            </div>

            <div className="border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-amber-900 font-semibold">
                  Offals in Freezer
                </div>
                <button
                  onClick={addOffalRow}
                  data-testid="fz-offal-add"
                  className="text-[10px] uppercase tracking-wider border border-amber-300 bg-white px-2 py-1 hover:bg-amber-100 flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Row
                </button>
              </div>
              <div className="space-y-2">
                {offals.map((o, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input
                      value={o.label}
                      onChange={(e) => updOffal(i, "label", e.target.value)}
                      data-testid={`fz-off-label-${i}`}
                      className="col-span-5 border border-amber-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                    <input
                      type="number"
                      step={o.unit === "piece" ? "1" : "0.01"}
                      placeholder={o.unit === "piece" ? "pcs" : "kg"}
                      value={o.qty}
                      onChange={(e) => updOffal(i, "qty", e.target.value)}
                      data-testid={`fz-off-qty-${i}`}
                      className="col-span-3 border border-amber-200 bg-white px-2 py-1.5 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                    <select
                      value={o.unit}
                      onChange={(e) => updOffal(i, "unit", e.target.value)}
                      data-testid={`fz-off-unit-${i}`}
                      className="col-span-3 border border-amber-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                    >
                      <option value="kg">Kg</option>
                      <option value="piece">Piece</option>
                    </select>
                    <button
                      onClick={() => removeOffalRow(i)}
                      className="col-span-1 border border-amber-200 bg-white hover:bg-rose-50 hover:text-rose-600 py-1.5"
                      data-testid={`fz-off-remove-${i}`}
                    >
                      <Trash2 className="h-3 w-3 mx-auto" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                  Sold from Freezer today (kg)
                </label>
                <input
                  type="number" step="0.01" value={soldKg}
                  onChange={(e) => setSoldKg(e.target.value)}
                  data-testid="fz-sold"
                  placeholder="Manually enter kg sold from freezer"
                  className="w-full border border-emerald-300 bg-emerald-50 px-3 py-2 mt-1 font-mono-num text-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Notes (optional)
                </label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="fz-notes"
                  placeholder="e.g. fresh stock for tomorrow"
                  className="w-full border border-zinc-300 px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
            </div>

            <button
              onClick={save}
              data-testid="fz-save"
              className="w-full bg-sky-600 text-white py-3 font-medium uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save Snapshot
            </button>
          </div>
        </div>

        {/* History */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Recent Snapshots
          </div>
          <div className="border border-zinc-200 bg-white" data-testid="fz-history">
            {history.length === 0 ? (
              <div className="p-5 text-sm text-zinc-500 text-center">No records yet</div>
            ) : (
              history.map((h) => (
                <div key={h.id} className="border-b border-zinc-100 last:border-b-0 px-4 py-3" data-testid={`fz-row-${h.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold">{fmtDate(h.date)}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 font-mono-num">
                        L:{fmtNum(h.live_chicken_kg)} · ChC:{fmtNum(h.chicken_carcass_kg)} · MtC:{fmtNum(h.mutton_carcass_kg)} kg
                      </div>
                      {(h.offals || []).length > 0 && (
                        <div className="text-[10px] text-zinc-500 mt-1 flex flex-wrap gap-x-2 font-mono-num">
                          {h.offals.map((o, idx) => (
                            <span key={idx}>{o.label}: {o.qty}{o.unit === "piece" ? "pc" : "kg"}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setDate(h.date)}
                        className="text-zinc-400 hover:text-sky-600 text-[10px] uppercase tracking-wider"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => del(h.id)}
                        className="text-zinc-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
